import * as React from "react";
import { Badge, type BadgeProps, type BadgeVariant } from "../atoms/badge.js";

/**
 * Lifecycle status vocabulary shared across product surfaces. Maps a semantic
 * status to a `Badge` variant so the green/amber/neutral convention is defined
 * once instead of hand-rolled per call site (dashboard team roster, wallet
 * coverage cards, people rows all mapped these identically before).
 *
 *   - `positive`  → success (green): active, completed, covered, enrolled
 *   - `pending`   → warning (amber): pending, in-progress, awaiting action
 *   - `inactive`  → neutral (grey):  ended, not-started, terminated
 *   - `invited`   → brand:           invited / awaiting account setup
 */
export type StatusTone = "positive" | "pending" | "inactive" | "invited";

const TONE_TO_VARIANT: Record<StatusTone, BadgeVariant> = {
  positive: "success",
  pending: "warning",
  inactive: "neutral",
  invited: "brand",
};

export interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  /** Semantic lifecycle status; drives the badge colour. */
  status: StatusTone;
}

/**
 * A `Badge` whose colour is derived from a semantic lifecycle status rather
 * than a raw variant, so every surface renders the same colour for the same
 * meaning. Pass the human label via `label` or `children` as usual.
 */
export const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(function StatusBadge(
  { status, ...props },
  ref,
) {
  return <Badge ref={ref} variant={TONE_TO_VARIANT[status]} {...props} />;
});

StatusBadge.displayName = "StatusBadge";
